import { ConsoleLogger } from "@aderant/aderant-web-fw-applications";
import React, { useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from "react";
import { useColumnOrder, useExpanded, useFilters, useFlexLayout, useGroupBy, usePagination, useResizeColumns, useRowSelect, useRowState, useSortBy, useTable } from "react-table";
import { v4 } from "uuid";
import { GridToolbar } from "../../../GridToolbar/GridToolbar";
import { useExpandAllOnFirstRender } from "../../../Utils/useExpandAllOnFirstRender";
import { excludeUnselectableRows, shouldExcludeUnselectableRows } from "../../functions/excludeUnselectableRows";
import { getUnselectableRowIds } from "../../functions/getUnselectableRowIds";
import { getSelectedDataRows, triggerOnSelectedRowsChanged } from "../../functions/triggerOnSelectedRowsChanged";
import { EditableGridActionTypes } from "../../state/EditableGridActions";
import { EditableGridReducer } from "../../state/EditableGridReducer";
import { gridError, isValid } from "../../types/GridError";
import GridContainer from "../GridContainer";
import { convertColumnDefinitions } from "./EditableGridColumnDefinition";
import EditableGridTable from "./EditableGridTable";
import { createEditableGridRowContext } from "./EditableRowRenderer";
EditableGrid.defaultProps = {
    options: {
        allowSelection: false,
        allowMoveColumns: true,
        allowPagination: false,
        skipPageReset: true,
        rowsPerPageOptions: [5, 10, 25]
    },
    style: {
        grid: {
            height: "100%"
        },
        rows: {
            rowHeight: 40
        }
    }
};
const logger = new ConsoleLogger();
/**
 * Converts the data in a row object to an object of type T, which is the source data type.
 * This is done by invoking transformCellValueOnRowEdit() on each property of the row data.
 */
const convertRowToDataObject = (source, dest, columnsById) => {
    logger.debug(`Converting row values to data values`);
    for (const key of Object.keys(source)) {
        //Iterate through each column to invoke transformCellValueOnRowEdit() on each cell value
        const columnToMap = columnsById.get(key);
        if (columnToMap?.isDataProperty == true) {
            if (columnToMap?.transformCellValueOnRowEdit) {
                try {
                    dest = columnToMap.transformCellValueOnRowEdit(source[key], dest);
                }
                catch (error) {
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    return gridError(`Failed to transform the grid cell value with error: ${error}`);
                }
            }
            else {
                //Checks if the column id exists as a property on the data
                if (key in dest) {
                    dest = { ...dest, [key]: source[key] };
                }
                else {
                    return gridError(`Cannot find key "${key}" in the row data. Please ensure the column id/path provided in the column definition correspond to a matching key in the data object.`);
                }
            }
        }
    }
    return dest;
};
/**Returns an object containing the default properties from parameter defaultNewRow.
 * @param defaultNewRow - an object containing default data values
 * @param columnDefinitions - a list of column definitions
 */
const getAddNewRowValues = (defaultNewRow, columnDefinitions) => {
    const newRowObject = {};
    if (defaultNewRow) {
        columnDefinitions.forEach((column) => {
            if (typeof column.path == "function" && column.id) {
                Object.assign(newRowObject, { [column.id]: column.path(defaultNewRow) });
            }
            else if (column.path && typeof column.path == "string" && column.path in defaultNewRow) {
                Object.assign(newRowObject, { [column.path]: defaultNewRow[column.path] });
            }
        });
    }
    return newRowObject;
};
/**Default column widths */
const defaultColumnWidths = { minWidth: 25, width: 200 };
/**
 * Editable Grid. Includes sorting, fixed grouping, pagination, row virtuailzation and selection.
 * @param props @see EditableGridProps
 *
 */
export function EditableGrid(props) {
    const { columnDefinitions, data, dataIsLoading, filters, editableProps = {}, options = {}, rowMetadata, 
    // Allows the grid to auto expand when the ID changes
    gridStateId } = props;
    const { getIsRowLoading, getIsRowReadOnly, getRowId, getRowProps } = rowMetadata;
    const { rowHeight, editableRowStyle, displayRowStyle } = { ...EditableGrid.defaultProps.style.rows, ...props.style?.rows };
    const gridStyle = { ...EditableGrid.defaultProps.style.grid, ...props.style?.grid };
    const { allowMoveColumns, allowPagination, allowSelection, defaultSelectedRowIds = [], rowsPerPageOptions, selectionActions, onSelectedRowsChanged, resetSelectedRowsOnDataChange = false, skipPageReset } = Object.assign({}, EditableGrid.defaultProps.options, options);
    const { defaultNewRow, onRowAdd, onRowDelete, onRowEndEdit, showAddNewRow = editableProps.onRowAdd ? true : false } = editableProps;
    const isEditable = Object.keys(editableProps).length > 0;
    const [previousUnselectableRowIds, setPreviousUnselectableRowIds] = useState([]);
    const [unselectableRowIds, setUnselectableRowIds] = useState([]);
    const columns = useMemo(() => convertColumnDefinitions(columnDefinitions), [columnDefinitions]);
    const columnsById = useMemo(() => new Map(columns.map((column) => [column.id, column])), [columns]);
    const [defaultNewRowValues, setDefaultRowValues] = useState(defaultNewRow ? getAddNewRowValues(defaultNewRow, columnDefinitions) : {});
    const [columnWidths, setColumnWidths] = useState({});
    const rootGridStateId = useRef(v4());
    //If no gridStateId is provided, use a root id so there will only be one state for the lifetime of the component.
    const uniqueGridStateId = gridStateId ? `${rootGridStateId.current},${gridStateId}` : rootGridStateId.current;
    useEffect(() => {
        setUnselectableRowIds(getUnselectableRowIds(data, getIsRowLoading, getIsRowReadOnly, getRowId));
    }, [data, getIsRowLoading, getIsRowReadOnly, getRowId]);
    useMemo(() => {
        if (isEditable == true) {
            const errorString = [];
            columnDefinitions.forEach((column) => {
                if (typeof column.path == "function" && column.transformCellValueOnRowEdit === undefined) {
                    errorString.push(column.id);
                }
            });
            if (errorString.length > 0) {
                //This is an exceptions to stop rendering as this is an error with regards to the consumption of the component.
                throw new Error(`A function was specified as the path for the following column/s ("${errorString.join()}"). Please provide a transformCellValueOnRowEdit() function to transform the entered cell value into a shape that matches the data type.`);
            }
        }
    }, [columnDefinitions, isEditable]);
    //Sets the default row values to be used in rendering the addNewRow
    useEffect(() => {
        if (defaultNewRow) {
            setDefaultRowValues(getAddNewRowValues(defaultNewRow, columnDefinitions));
        }
    }, [defaultNewRow, columnDefinitions]);
    // #region Internal grid state
    const [{ addNewRowData, rowIndexOnEdit, rowDataOnEdit }, gridDispatch] = useReducer(EditableGridReducer, {
        addNewRowData: defaultNewRowValues,
        rowIndexOnEdit: undefined,
        rowDataOnEdit: undefined
    });
    // #endregion
    // #region EditableProps handlers
    const onRowStartingEdit = (rowIndex) => {
        logger.debug(`Setting row with index "${rowIndex}" to edit mode.`);
        const rowData = Object.assign({}, flatRows.find((r) => r.index == rowIndex)?.values);
        gridDispatch({ type: EditableGridActionTypes.SET_ROW_TO_EDIT, payload: { rowIndex: rowIndex, rowData: rowData } });
    };
    const onRowEndingEdit = async () => {
        if (onRowEndEdit) {
            if (rowIndexOnEdit != undefined && rowDataOnEdit != undefined) {
                const dataRow = Object.assign({}, data[rowIndexOnEdit]);
                //We need to transform each cell value into a property on the source data as the column path may not directly map to a property in the source object.
                //Example: dataRow:{a: 1, b: 2, c: 3}, rowDataOnEdit:{a: 1, d: 3}
                const result = convertRowToDataObject(rowDataOnEdit, dataRow, columnsById);
                if (isValid(result)) {
                    logger.debug(`Executing onRowEndEdit() on row with index ${rowIndexOnEdit}.`);
                    await onRowEndEdit(rowIndexOnEdit, result)
                        .then(() => {
                        gridDispatch({ type: EditableGridActionTypes.RESET_INDEX_ON_EDIT });
                    })
                        .catch((error) => {
                        logger.info(`Failed to edit row with error: ${error}`);
                    });
                }
                else {
                    logger.warn(result.reason);
                }
            }
        }
        else {
            gridDispatch({ type: EditableGridActionTypes.RESET_INDEX_ON_EDIT });
            logger.warn("endRowEdit() was invoked but the onRowEndEdit() function prop is missing. Row data is unchanged.");
        }
    };
    const onRowStartingAdd = async (newRowData) => {
        if (onRowAdd && defaultNewRow) {
            const dataToAdd = newRowData ?? addNewRowData;
            const result = convertRowToDataObject(dataToAdd, defaultNewRow, columnsById);
            if (isValid(result)) {
                logger.debug(`Executing onAddRow()`);
                await onRowAdd(result)
                    .then(() => {
                    gridDispatch({ type: EditableGridActionTypes.UPDATE_ADD_NEW_ROW_DATA, rowData: defaultNewRowValues });
                    gridDispatch({ type: EditableGridActionTypes.INCREMENT_INDEX_ON_EDIT });
                })
                    .catch((error) => {
                    logger.warn(`Failed to add row with error: ${error}`);
                });
            }
            else {
                logger.warn(result.reason);
            }
        }
        else {
            logger.warn("addRow() was invoked but the onRowAdd() function prop and defaultNewRow are missing. Data source is unchanged.");
        }
    };
    const onRowEndingDelete = async (rowIndex, originalRow) => {
        if (onRowDelete) {
            logger.debug(`Executing onRowDelete() on row with index ${rowIndex}.`);
            await onRowDelete(rowIndex, originalRow)
                .then(() => {
                if (rowIndexOnEdit && rowIndex < rowIndexOnEdit) {
                    gridDispatch({ type: EditableGridActionTypes.DECREMENT_INDEX_ON_EDIT });
                }
                else if (rowIndex == rowIndexOnEdit) {
                    gridDispatch({ type: EditableGridActionTypes.RESET_INDEX_ON_EDIT });
                }
            })
                .catch((error) => {
                logger.warn(`Failed to delete row with error: ${error}`);
            });
        }
        else {
            logger.warn("deleteRow() was invoked but the onRowDelete() function prop and defaultNewRow are missing. Data source is unchanged.");
        }
    };
    const onRowUpdate = (columnId, value) => {
        logger.debug(`Updating "${columnId}" value in row.`);
        const updatedRowData = Object.assign({}, rowDataOnEdit);
        updatedRowData[columnId] = value;
        gridDispatch({ type: EditableGridActionTypes.UPDATE_EDIT_ROW_DATA, rowData: updatedRowData });
    };
    //#endregion
    // #region React Table Hooks
    const reactTableProps = useTable({
        autoResetExpanded: false,
        autoResetSortBy: false,
        autoResetFilters: false,
        autoResetSelectedRows: resetSelectedRowsOnDataChange,
        columns,
        data,
        disableResizing: isEditable,
        defaultCanFilter: true,
        defaultColumn: defaultColumnWidths,
        getRowId,
        autoResetPage: !skipPageReset,
        initialState: {
            selectedRowIds: defaultSelectedRowIds.reduce((acc, id) => {
                acc[id] = true;
                return acc;
            }, {}),
            groupBy: useMemo(() => columns.filter((col) => col.isGrouped == true).map((col) => col.id || ""), [columns]),
            hiddenColumns: useMemo(() => columns.filter((col) => col.isVisible == false).map((col) => col.id || ""), [columns])
        },
        stateReducer: (newState, action, prevState) => {
            if (action.type === "init" || action.type === "toggleRowSelected" || action.type === "toggleAllRowsSelected" || action.type === "excludeUnselectableRows") {
                const selectedRowIds = excludeUnselectableRows(newState.selectedRowIds, unselectableRowIds);
                setPreviousUnselectableRowIds(unselectableRowIds);
                return { ...newState, selectedRowIds: selectedRowIds };
            }
            return newState;
        },
        useControlledState: (state) => {
            return useMemo(() => ({
                ...state,
                addNewRowData: addNewRowData,
                rowIndexOnEdit: rowIndexOnEdit,
                rowDataOnEdit: rowDataOnEdit,
                //justification: state.columnResizing.columnWidths is an any type in React table types
                //eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                headerWidths: { ...columnWidths, ...state.columnResizing.columnWidths },
                columnResizing: {
                    ...state.columnResizing,
                    //eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    columnWidths: Object.keys(state.columnResizing.columnWidths).length == 0 ? columnWidths : state.columnResizing.columnWidths
                }
            }), [state, addNewRowData, columnWidths, rowIndexOnEdit, rowDataOnEdit]);
        },
        startRowEdit: onRowStartingEdit,
        endRowEdit: onRowEndingEdit,
        cancelRowEdit: () => gridDispatch({ type: EditableGridActionTypes.RESET_INDEX_ON_EDIT }),
        addRow: onRowStartingAdd,
        updateRow: onRowUpdate,
        deleteRow: onRowEndingDelete
    }, useFlexLayout, useColumnOrder, useFilters, useGroupBy, useSortBy, useExpanded, usePagination, useRowSelect, useRowState, useResizeColumns);
    // #endregion
    const { dispatch: reactTableDispatch, flatRows, headerGroups, page, prepareRow, rows, getTableProps, getTableBodyProps, getToggleAllRowsSelectedProps, gotoPage, setColumnOrder, setPageSize, setAllFilters, state, toggleAllRowsSelected, toggleAllRowsExpanded, visibleColumns, expandedRows } = reactTableProps;
    // #endregion
    useExpandAllOnFirstRender(uniqueGridStateId, expandedRows, toggleAllRowsExpanded);
    //Identify actual column widths on initial render only
    useLayoutEffect(() => {
        setColumnWidths((prevColumnWidths) => {
            const newColumnwidths = { ...prevColumnWidths };
            columns.forEach((column) => {
                const width = document.querySelector(`#header-${column.id}`)?.getBoundingClientRect().width;
                newColumnwidths[column.id] = column.pxWidth ?? width ?? defaultColumnWidths.width;
            });
            return newColumnwidths;
        });
    }, []);
    useEffect(() => {
        if (shouldExcludeUnselectableRows(allowSelection, previousUnselectableRowIds, unselectableRowIds)) {
            //Dispatch custom action to trigger a refresh for the selected rows state as we want to exclude already selected items if they become unselectable
            reactTableDispatch({ type: "excludeUnselectableRows" });
        }
    }, [unselectableRowIds]);
    useEffect(() => {
        triggerOnSelectedRowsChanged(allowSelection, onSelectedRowsChanged, data, state.selectedRowIds, getRowId);
    }, [getRowId, state.selectedRowIds, allowSelection]);
    useEffect(() => {
        if (filters) {
            setAllFilters(filters);
        }
    }, [filters]);
    const numSelectedRows = getSelectedDataRows(data, state.selectedRowIds, getRowId).length;
    const getColumnWidth = (columnId) => {
        return state.headerWidths[columnId];
    };
    const EditableGridRowContext = createEditableGridRowContext();
    const rowContext = useMemo(() => ({
        allowSelection,
        columns,
        displayRowStyle,
        editableRowStyle,
        getColumnWidth,
        getRowProps,
        getIsRowLoading,
        getIsRowReadOnly,
        prepareRow
    }), [allowSelection, columns, displayRowStyle, editableRowStyle, getColumnWidth, getIsRowLoading, getIsRowReadOnly, getRowProps, prepareRow]);
    // #region Renders the entire grid
    return (React.createElement(GridContainer, { paginationProps: {
            allowPagination: allowPagination,
            dataCount: data.length,
            page: state.pageIndex,
            rowCount: rows.length,
            rowsPerPage: state.pageSize,
            rowsPerPageOptions: rowsPerPageOptions,
            gotoPage: gotoPage,
            setPageSize: setPageSize
        }, style: gridStyle },
        React.createElement(GridToolbar, { numSelected: numSelectedRows, selectionActions: selectionActions, onClickDeselectRows: () => toggleAllRowsSelected(false) }, props.toolbar),
        React.createElement(EditableGridRowContext.Provider, { value: rowContext },
            React.createElement("div", { style: { display: "flex", flexGrow: 2, overflow: "hidden" } },
                React.createElement(EditableGridTable, { allowSelection: allowSelection, allowMoveColumns: allowMoveColumns, allowPagination: allowPagination, defaultNewRow: defaultNewRow, editableRowStyle: editableRowStyle, getColumnWidth: getColumnWidth, gridDispatch: gridDispatch, isEditable: isEditable, onRowStartingAdd: onRowStartingAdd, rowHeight: rowHeight, rowIndexOnEdit: rowIndexOnEdit, showAddNewRow: showAddNewRow, dataIsLoading: dataIsLoading, headerGroups: headerGroups, getTableProps: getTableProps, getTableBodyProps: getTableBodyProps, getToggleAllRowsSelectedProps: getToggleAllRowsSelectedProps, page: page, rows: rows, setColumnOrder: setColumnOrder, visibleColumns: visibleColumns, state: state })))));
    // #endregion
}
